import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Divider,
  Grid,
  TextField,
  Typography,
  Slider,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Paper,
  Tabs,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@material-ui/core'
import { Alert } from '@material-ui/lab'
import {
  PlayArrow as PlayIcon,
  Settings as SettingsIcon,
  Functions as CalculateIcon
} from '@material-ui/icons'
import axios from 'axios'

const CalculationPlayground = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [configs, setConfigs] = useState([])
  const [selectedConfig, setSelectedConfig] = useState(null)
  const [loading, setLoading] = useState(true)

  // Plant calculation inputs
  const [plantInputs, setPlantInputs] = useState({
    size: 'mediumPlants',
    quantity: 100,
    difficulty: 'standard',
    soilType: 'loamy',
    season: 'spring'
  })

  // Gravel calculation inputs
  const [gravelInputs, setGravelInputs] = useState({
    type: 'halfInch',
    squareFeet: 1000,
    depth: 2,
    installMethod: 'hand',
    difficulty: 'standard'
  })

  // Irrigation calculation inputs
  const [irrigationInputs, setIrrigationInputs] = useState({
    plantCount: 120,
    treeCount: 12,
    includeBackflow: true,
    includeClock: true
  })

  // Lighting calculation inputs
  const [lightingInputs, setLightingInputs] = useState({
    lightCount: 20,
    wattsPerLight: 10
  })

  // Temporary config overrides (for live editing)
  const [configOverrides, setConfigOverrides] = useState({})

  useEffect(() => {
    loadConfigs()
  }, [])

  const loadConfigs = async () => {
    try {
      setLoading(true)
      const response = await axios.get('/api/calculation-configs')
      setConfigs(response.data.configs || [])

      // Try to load user's preferred config
      const prefResponse = await axios.get('/api/calculation-configs/user/preference')
      const preferredConfigId = prefResponse.data.configId
      const preferredConfig = response.data.configs.find(c => c._id === preferredConfigId)

      if (preferredConfig) {
        setSelectedConfig(preferredConfig)
      } else if (response.data.configs.length > 0) {
        setSelectedConfig(response.data.configs[0])
      }
    } catch (err) {
      console.error('Error loading configs:', err)
    } finally {
      setLoading(false)
    }
  }

  const getConfigValue = (path) => {
    // Check overrides first
    if (configOverrides[path] !== undefined) {
      return configOverrides[path]
    }

    // Otherwise get from selected config
    if (!selectedConfig) return 0

    const parts = path.split('.')
    let value = selectedConfig
    for (const part of parts) {
      value = value?.[part]
    }
    return value || 0
  }

  const setConfigValue = (path, value) => {
    setConfigOverrides(prev => ({
      ...prev,
      [path]: parseFloat(value) || 0
    }))
  }

  // Plant calculation logic
  const calculatePlants = () => {
    const rate = getConfigValue(`plantRates.${plantInputs.size}`)
    const difficultyFactor = getConfigValue(`difficultyFactors.${plantInputs.difficulty}`)
    const soilFactor = getConfigValue(`soilFactors.${plantInputs.soilType}`)
    const seasonalFactor = getConfigValue(`seasonalFactors.${plantInputs.season}`)
    const dayRate = getConfigValue('labor.dayRate')

    const plantsPerDay = rate
    const adjustedRate = plantsPerDay / (difficultyFactor * soilFactor * seasonalFactor)
    const laborDays = plantInputs.quantity / adjustedRate
    const laborCost = laborDays * dayRate

    return {
      plantsPerDay: plantsPerDay.toFixed(0),
      difficultyFactor: difficultyFactor.toFixed(2),
      soilFactor: soilFactor.toFixed(2),
      seasonalFactor: seasonalFactor.toFixed(2),
      adjustedRate: adjustedRate.toFixed(1),
      laborDays: laborDays.toFixed(2),
      dayRate: dayRate.toFixed(2),
      laborCost: laborCost.toFixed(2)
    }
  }

  // Gravel calculation logic
  const calculateGravel = () => {
    const coverage = getConfigValue(`gravelCoverage.${gravelInputs.type}`)
    const installRate = gravelInputs.installMethod === 'hand'
      ? getConfigValue('materialRates.gravelTonsPerDay')
      : gravelInputs.installMethod === 'tractor'
        ? getConfigValue('moveGravelRates.gravelTonsPerDayTractor')
        : getConfigValue('moveGravelRates.gravelTonsPerDayBobcat')

    const dayRate = getConfigValue('labor.dayRate')
    const equipmentRate = gravelInputs.installMethod === 'tractor'
      ? getConfigValue('equipment.tractorRentalDailyRate')
      : gravelInputs.installMethod === 'bobcat'
        ? getConfigValue('equipment.bobcatRentalDailyRate')
        : 0

    const difficultyFactor = getConfigValue(`difficultyFactors.${gravelInputs.difficulty}`)

    // Calculate depth multiplier (2" is standard)
    const depthMultiplier = gravelInputs.depth / 2

    const tonsNeeded = (gravelInputs.squareFeet / coverage) * depthMultiplier
    const adjustedRate = installRate / difficultyFactor
    const laborDays = tonsNeeded / adjustedRate

    const materialCost = tonsNeeded >= 10
      ? tonsNeeded * getConfigValue('gravel.costPerTonOver9')
      : tonsNeeded * getConfigValue('gravel.costPerTonUnder9')

    const laborCost = laborDays * dayRate
    const equipmentCost = laborDays * equipmentRate
    const totalCost = materialCost + laborCost + equipmentCost

    return {
      coverage: coverage.toFixed(0),
      tonsNeeded: tonsNeeded.toFixed(2),
      installRate: installRate.toFixed(1),
      difficultyFactor: difficultyFactor.toFixed(2),
      adjustedRate: adjustedRate.toFixed(1),
      laborDays: laborDays.toFixed(2),
      materialCost: materialCost.toFixed(2),
      laborCost: laborCost.toFixed(2),
      equipmentCost: equipmentCost.toFixed(2),
      totalCost: totalCost.toFixed(2)
    }
  }

  // Irrigation calculation logic
  const calculateIrrigation = () => {
    const plantsPerValve = getConfigValue('irrigation.plantsPerValve')
    const treesPerValve = getConfigValue('irrigation.treesPerValve')
    const daysPerValve = getConfigValue('irrigation.daysPerValve')
    const costPerValve = getConfigValue('irrigation.costPerValve')
    const clockCost = getConfigValue('irrigation.clockCost')
    const backflowCost = getConfigValue('irrigation.backflowCost')
    const dayRate = getConfigValue('labor.dayRate')

    const plantValves = Math.ceil(irrigationInputs.plantCount / plantsPerValve)
    const treeValves = Math.ceil(irrigationInputs.treeCount / treesPerValve)
    const totalValves = plantValves + treeValves

    const materialCost = (totalValves * costPerValve) +
      (irrigationInputs.includeClock ? clockCost : 0) +
      (irrigationInputs.includeBackflow ? backflowCost : 0)

    const laborDays = totalValves * daysPerValve
    const laborCost = laborDays * dayRate
    const totalCost = materialCost + laborCost

    return {
      plantValves,
      treeValves,
      totalValves,
      daysPerValve: daysPerValve.toFixed(2),
      laborDays: laborDays.toFixed(2),
      materialCost: materialCost.toFixed(2),
      laborCost: laborCost.toFixed(2),
      totalCost: totalCost.toFixed(2)
    }
  }

  // Lighting calculation logic
  const calculateLighting = () => {
    const costPerLight = getConfigValue('lighting.costPerLight')
    const techDailyRate = getConfigValue('lighting.techDailyRate')
    const lightsPerDay = getConfigValue('lighting.lightsInstalledPerDay')
    const hoursTrenchingPerLight = getConfigValue('lighting.hoursTrenchingPerLight')
    const wiringCostPerLight = getConfigValue('lighting.wiringAndConnectorCostPerLight')
    const hourlyTrenchingRate = getConfigValue('lighting.hourlyTrenchingRate')

    // Calculate total wattage and transformer
    const totalWattage = lightingInputs.lightCount * lightingInputs.wattsPerLight
    let transformerCost = 0
    let transformerSize = ''

    if (totalWattage <= 150) {
      transformerCost = getConfigValue('lighting.transformerCosts.w150')
      transformerSize = '150W'
    } else if (totalWattage <= 300) {
      transformerCost = getConfigValue('lighting.transformerCosts.w300')
      transformerSize = '300W'
    } else if (totalWattage <= 600) {
      transformerCost = getConfigValue('lighting.transformerCosts.w600')
      transformerSize = '600W'
    } else if (totalWattage <= 900) {
      transformerCost = getConfigValue('lighting.transformerCosts.w900')
      transformerSize = '900W'
    } else if (totalWattage <= 1200) {
      transformerCost = getConfigValue('lighting.transformerCosts.w1200')
      transformerSize = '1200W'
    } else {
      transformerCost = getConfigValue('lighting.transformerCosts.custom')
      transformerSize = 'Custom'
    }

    const lightMaterialCost = lightingInputs.lightCount * costPerLight
    const wiringCost = lightingInputs.lightCount * wiringCostPerLight
    const installDays = lightingInputs.lightCount / lightsPerDay
    const trenchingHours = lightingInputs.lightCount * hoursTrenchingPerLight

    const materialCost = lightMaterialCost + wiringCost + transformerCost
    const installCost = installDays * techDailyRate
    const trenchingCost = trenchingHours * hourlyTrenchingRate
    const totalCost = materialCost + installCost + trenchingCost

    return {
      totalWattage,
      transformerSize,
      transformerCost: transformerCost.toFixed(2),
      installDays: installDays.toFixed(2),
      trenchingHours: trenchingHours.toFixed(2),
      materialCost: materialCost.toFixed(2),
      installCost: installCost.toFixed(2),
      trenchingCost: trenchingCost.toFixed(2),
      totalCost: totalCost.toFixed(2)
    }
  }

  const results = activeTab === 0 ? calculatePlants() :
                  activeTab === 1 ? calculateGravel() :
                  activeTab === 2 ? calculateIrrigation() :
                  calculateLighting()

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading configurations...</Typography>
      </Box>
    )
  }

  return (
    <Box style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingBottom: 40 }}>
      <Container maxWidth="xl" style={{ paddingTop: 32 }}>
        {/* Header */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          style={{
            marginBottom: 32,
            padding: '24px 32px',
            backgroundColor: '#fff',
            borderRadius: 8,
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          <Box>
            <Typography variant="h4" component="h1" style={{ fontWeight: 500, marginBottom: 8 }}>
              <CalculateIcon style={{ verticalAlign: 'middle', marginRight: 12 }} />
              Calculation Playground
            </Typography>
            <Typography variant="body2" color="textSecondary">
              Test and fine-tune your calculation configurations with real-time results
            </Typography>
          </Box>
          <FormControl variant="outlined" style={{ minWidth: 300 }}>
            <InputLabel>Configuration</InputLabel>
            <Select
              value={selectedConfig?._id || ''}
              onChange={(e) => {
                const config = configs.find(c => c._id === e.target.value)
                setSelectedConfig(config)
                setConfigOverrides({}) // Reset overrides when switching configs
              }}
              label="Configuration"
            >
              {configs.map(config => (
                <MenuItem key={config._id} value={config._id}>
                  {config.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {!selectedConfig ? (
          <Alert severity="warning">
            Please select a configuration to get started
          </Alert>
        ) : (
          <Grid container spacing={3}>
            {/* Left Panel - Inputs */}
            <Grid item xs={12} lg={6}>
              <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%' }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, newValue) => setActiveTab(newValue)}
                  indicatorColor="primary"
                  textColor="primary"
                  variant="fullWidth"
                  style={{ borderBottom: '1px solid rgba(0,0,0,0.12)' }}
                >
                  <Tab label="Plants" />
                  <Tab label="Gravel" />
                  <Tab label="Irrigation" />
                  <Tab label="Lighting" />
                </Tabs>

                <CardContent style={{ padding: 32 }}>
                  {/* Plant Inputs */}
                  {activeTab === 0 && (
                    <Box>
                      <Typography variant="h6" gutterBottom style={{ fontWeight: 500, marginBottom: 24 }}>
                        Plant Installation Test
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Plant Size</InputLabel>
                            <Select
                              value={plantInputs.size}
                              onChange={(e) => setPlantInputs({...plantInputs, size: e.target.value})}
                              label="Plant Size"
                            >
                              <MenuItem value="flatFlowers">Flat Flowers</MenuItem>
                              <MenuItem value="pintFlowers">Pint Flowers</MenuItem>
                              <MenuItem value="quartFlowers">Quart Flowers</MenuItem>
                              <MenuItem value="smallPlants">Small Plants (#1)</MenuItem>
                              <MenuItem value="mediumPlants">Medium Plants (#5)</MenuItem>
                              <MenuItem value="largePlants">Large Plants (#15)</MenuItem>
                              <MenuItem value="mediumTrees">Medium Trees (24")</MenuItem>
                              <MenuItem value="largeTrees">Large Trees (36"+)</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography gutterBottom>Quantity: {plantInputs.quantity}</Typography>
                          <Slider
                            value={plantInputs.quantity}
                            onChange={(e, value) => setPlantInputs({...plantInputs, quantity: value})}
                            min={1}
                            max={500}
                            valueLabelDisplay="auto"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                              value={plantInputs.difficulty}
                              onChange={(e) => setPlantInputs({...plantInputs, difficulty: e.target.value})}
                              label="Difficulty"
                            >
                              <MenuItem value="veryEasy">Very Easy</MenuItem>
                              <MenuItem value="standard">Standard</MenuItem>
                              <MenuItem value="slightlyDifficult">Slightly Difficult</MenuItem>
                              <MenuItem value="difficult">Difficult</MenuItem>
                              <MenuItem value="veryDifficult">Very Difficult</MenuItem>
                              <MenuItem value="extremelyDifficult">Extremely Difficult</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Soil Type</InputLabel>
                            <Select
                              value={plantInputs.soilType}
                              onChange={(e) => setPlantInputs({...plantInputs, soilType: e.target.value})}
                              label="Soil Type"
                            >
                              <MenuItem value="sandy">Sandy</MenuItem>
                              <MenuItem value="loamy">Loamy</MenuItem>
                              <MenuItem value="clay">Clay</MenuItem>
                              <MenuItem value="rocky">Rocky</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Season</InputLabel>
                            <Select
                              value={plantInputs.season}
                              onChange={(e) => setPlantInputs({...plantInputs, season: e.target.value})}
                              label="Season"
                            >
                              <MenuItem value="spring">Spring</MenuItem>
                              <MenuItem value="summer">Summer</MenuItem>
                              <MenuItem value="fall">Fall</MenuItem>
                              <MenuItem value="winter">Winter</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Divider style={{ margin: '32px 0' }} />

                      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                        Quick Config Adjustments
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Base Rate (plants/day)"
                            value={getConfigValue(`plantRates.${plantInputs.size}`)}
                            onChange={(e) => setConfigValue(`plantRates.${plantInputs.size}`, e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Labor Day Rate ($)"
                            value={getConfigValue('labor.dayRate')}
                            onChange={(e) => setConfigValue('labor.dayRate', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 10 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Difficulty Multiplier"
                            value={getConfigValue(`difficultyFactors.${plantInputs.difficulty}`)}
                            onChange={(e) => setConfigValue(`difficultyFactors.${plantInputs.difficulty}`, e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 0.1 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Gravel Inputs */}
                  {activeTab === 1 && (
                    <Box>
                      <Typography variant="h6" gutterBottom style={{ fontWeight: 500, marginBottom: 24 }}>
                        Gravel/Rip-Rap Test
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Gravel Type</InputLabel>
                            <Select
                              value={gravelInputs.type}
                              onChange={(e) => setGravelInputs({...gravelInputs, type: e.target.value})}
                              label="Gravel Type"
                            >
                              <MenuItem value="quarterInch">1/4 Inch</MenuItem>
                              <MenuItem value="halfInch">1/2 Inch</MenuItem>
                              <MenuItem value="threeQuarterInch">3/4 Inch</MenuItem>
                              <MenuItem value="oneInch">1 Inch</MenuItem>
                              <MenuItem value="ripRap">Rip-Rap</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                          <Typography gutterBottom>Square Feet: {gravelInputs.squareFeet}</Typography>
                          <Slider
                            value={gravelInputs.squareFeet}
                            onChange={(e, value) => setGravelInputs({...gravelInputs, squareFeet: value})}
                            min={100}
                            max={5000}
                            step={100}
                            valueLabelDisplay="auto"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography gutterBottom>Depth (inches): {gravelInputs.depth}"</Typography>
                          <Slider
                            value={gravelInputs.depth}
                            onChange={(e, value) => setGravelInputs({...gravelInputs, depth: value})}
                            min={1}
                            max={6}
                            step={0.5}
                            marks
                            valueLabelDisplay="auto"
                          />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Installation Method</InputLabel>
                            <Select
                              value={gravelInputs.installMethod}
                              onChange={(e) => setGravelInputs({...gravelInputs, installMethod: e.target.value})}
                              label="Installation Method"
                            >
                              <MenuItem value="hand">By Hand</MenuItem>
                              <MenuItem value="tractor">With Tractor</MenuItem>
                              <MenuItem value="bobcat">With Bobcat</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth variant="outlined">
                            <InputLabel>Difficulty</InputLabel>
                            <Select
                              value={gravelInputs.difficulty}
                              onChange={(e) => setGravelInputs({...gravelInputs, difficulty: e.target.value})}
                              label="Difficulty"
                            >
                              <MenuItem value="veryEasy">Very Easy</MenuItem>
                              <MenuItem value="standard">Standard</MenuItem>
                              <MenuItem value="slightlyDifficult">Slightly Difficult</MenuItem>
                              <MenuItem value="difficult">Difficult</MenuItem>
                              <MenuItem value="veryDifficult">Very Difficult</MenuItem>
                              <MenuItem value="extremelyDifficult">Extremely Difficult</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Divider style={{ margin: '32px 0' }} />

                      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                        Quick Config Adjustments
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Coverage (sq ft/ton @ 2in)"
                            value={getConfigValue(`gravelCoverage.${gravelInputs.type}`)}
                            onChange={(e) => setConfigValue(`gravelCoverage.${gravelInputs.type}`, e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 5 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Cost per Ton ($)"
                            value={getConfigValue('gravel.costPerTonOver9')}
                            onChange={(e) => setConfigValue('gravel.costPerTonOver9', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 5 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Irrigation Inputs */}
                  {activeTab === 2 && (
                    <Box>
                      <Typography variant="h6" gutterBottom style={{ fontWeight: 500, marginBottom: 24 }}>
                        Irrigation System Test
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography gutterBottom>Plant Count: {irrigationInputs.plantCount}</Typography>
                          <Slider
                            value={irrigationInputs.plantCount}
                            onChange={(e, value) => setIrrigationInputs({...irrigationInputs, plantCount: value})}
                            min={0}
                            max={500}
                            step={10}
                            valueLabelDisplay="auto"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Typography gutterBottom>Tree Count: {irrigationInputs.treeCount}</Typography>
                          <Slider
                            value={irrigationInputs.treeCount}
                            onChange={(e, value) => setIrrigationInputs({...irrigationInputs, treeCount: value})}
                            min={0}
                            max={50}
                            step={1}
                            valueLabelDisplay="auto"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <FormControl component="fieldset">
                            <Box display="flex" gap={2}>
                              <Button
                                variant={irrigationInputs.includeBackflow ? "contained" : "outlined"}
                                color="primary"
                                onClick={() => setIrrigationInputs({...irrigationInputs, includeBackflow: !irrigationInputs.includeBackflow})}
                              >
                                Include Backflow
                              </Button>
                              <Button
                                variant={irrigationInputs.includeClock ? "contained" : "outlined"}
                                color="primary"
                                onClick={() => setIrrigationInputs({...irrigationInputs, includeClock: !irrigationInputs.includeClock})}
                              >
                                Include Clock
                              </Button>
                            </Box>
                          </FormControl>
                        </Grid>
                      </Grid>

                      <Divider style={{ margin: '32px 0' }} />

                      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                        Quick Config Adjustments
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Plants per Valve"
                            value={getConfigValue('irrigation.plantsPerValve')}
                            onChange={(e) => setConfigValue('irrigation.plantsPerValve', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 10 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Cost per Valve ($)"
                            value={getConfigValue('irrigation.costPerValve')}
                            onChange={(e) => setConfigValue('irrigation.costPerValve', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 50 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}

                  {/* Lighting Inputs */}
                  {activeTab === 3 && (
                    <Box>
                      <Typography variant="h6" gutterBottom style={{ fontWeight: 500, marginBottom: 24 }}>
                        Landscape Lighting Test
                      </Typography>

                      <Grid container spacing={3}>
                        <Grid item xs={12}>
                          <Typography gutterBottom>Number of Lights: {lightingInputs.lightCount}</Typography>
                          <Slider
                            value={lightingInputs.lightCount}
                            onChange={(e, value) => setLightingInputs({...lightingInputs, lightCount: value})}
                            min={1}
                            max={100}
                            step={1}
                            valueLabelDisplay="auto"
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Watts per Light"
                            value={lightingInputs.wattsPerLight}
                            onChange={(e) => setLightingInputs({...lightingInputs, wattsPerLight: parseFloat(e.target.value) || 0})}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                          />
                        </Grid>
                      </Grid>

                      <Divider style={{ margin: '32px 0' }} />

                      <Typography variant="subtitle1" gutterBottom style={{ fontWeight: 500 }}>
                        Quick Config Adjustments
                      </Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Cost per Light ($)"
                            value={getConfigValue('lighting.costPerLight')}
                            onChange={(e) => setConfigValue('lighting.costPerLight', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 10 } }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            variant="outlined"
                            type="number"
                            label="Lights per Day"
                            value={getConfigValue('lighting.lightsInstalledPerDay')}
                            onChange={(e) => setConfigValue('lighting.lightsInstalledPerDay', e.target.value)}
                            InputProps={{ inputProps: { min: 0, step: 1 } }}
                          />
                        </Grid>
                      </Grid>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>

            {/* Right Panel - Results */}
            <Grid item xs={12} lg={6}>
              <Card style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.1)', height: '100%' }}>
                <CardContent style={{ padding: 32 }}>
                  <Typography variant="h6" gutterBottom style={{ fontWeight: 500, marginBottom: 24 }}>
                    <PlayIcon style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    Calculation Results
                  </Typography>

                  {/* Plant Results */}
                  {activeTab === 0 && (
                    <Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell><strong>Metric</strong></TableCell>
                              <TableCell align="right"><strong>Value</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Base Rate (plants/day)</TableCell>
                              <TableCell align="right">{results.plantsPerDay}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Difficulty Factor</TableCell>
                              <TableCell align="right">{results.difficultyFactor}x</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Soil Factor</TableCell>
                              <TableCell align="right">{results.soilFactor}x</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Seasonal Factor</TableCell>
                              <TableCell align="right">{results.seasonalFactor}x</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#fff3e0' }}>
                              <TableCell><strong>Adjusted Rate (plants/day)</strong></TableCell>
                              <TableCell align="right"><strong>{results.adjustedRate}</strong></TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                              <TableCell><strong>Labor Days Required</strong></TableCell>
                              <TableCell align="right"><strong>{results.laborDays}</strong></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Day Rate</TableCell>
                              <TableCell align="right">${results.dayRate}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#c8e6c9' }}>
                              <TableCell><strong>Total Labor Cost</strong></TableCell>
                              <TableCell align="right"><strong>${results.laborCost}</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box mt={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Formula:</strong> Labor Days = Quantity ÷ (Base Rate ÷ (Difficulty × Soil × Season))
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Gravel Results */}
                  {activeTab === 1 && (
                    <Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell><strong>Metric</strong></TableCell>
                              <TableCell align="right"><strong>Value</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Coverage (sq ft/ton @ 2")</TableCell>
                              <TableCell align="right">{results.coverage}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#fff3e0' }}>
                              <TableCell><strong>Tons Needed</strong></TableCell>
                              <TableCell align="right"><strong>{results.tonsNeeded}</strong></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Installation Rate (tons/day)</TableCell>
                              <TableCell align="right">{results.installRate}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Difficulty Factor</TableCell>
                              <TableCell align="right">{results.difficultyFactor}x</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Adjusted Rate (tons/day)</TableCell>
                              <TableCell align="right">{results.adjustedRate}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                              <TableCell><strong>Labor Days</strong></TableCell>
                              <TableCell align="right"><strong>{results.laborDays}</strong></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Material Cost</TableCell>
                              <TableCell align="right">${results.materialCost}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Labor Cost</TableCell>
                              <TableCell align="right">${results.laborCost}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Equipment Cost</TableCell>
                              <TableCell align="right">${results.equipmentCost}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#c8e6c9' }}>
                              <TableCell><strong>Total Cost</strong></TableCell>
                              <TableCell align="right"><strong>${results.totalCost}</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box mt={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Formula:</strong> Tons = (Sq Ft ÷ Coverage) × (Depth ÷ 2)<br />
                          Labor Days = Tons ÷ (Rate ÷ Difficulty Factor)
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Irrigation Results */}
                  {activeTab === 2 && (
                    <Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell><strong>Metric</strong></TableCell>
                              <TableCell align="right"><strong>Value</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Plant Valves</TableCell>
                              <TableCell align="right">{results.plantValves}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Tree Valves</TableCell>
                              <TableCell align="right">{results.treeValves}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#fff3e0' }}>
                              <TableCell><strong>Total Valves</strong></TableCell>
                              <TableCell align="right"><strong>{results.totalValves}</strong></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Days per Valve</TableCell>
                              <TableCell align="right">{results.daysPerValve}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                              <TableCell><strong>Labor Days</strong></TableCell>
                              <TableCell align="right"><strong>{results.laborDays}</strong></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Material Cost</TableCell>
                              <TableCell align="right">${results.materialCost}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Labor Cost</TableCell>
                              <TableCell align="right">${results.laborCost}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#c8e6c9' }}>
                              <TableCell><strong>Total Cost</strong></TableCell>
                              <TableCell align="right"><strong>${results.totalCost}</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box mt={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Formula:</strong> Valves = ⌈Plants ÷ Plants/Valve⌉ + ⌈Trees ÷ Trees/Valve⌉
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {/* Lighting Results */}
                  {activeTab === 3 && (
                    <Box>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow style={{ backgroundColor: '#f5f5f5' }}>
                              <TableCell><strong>Metric</strong></TableCell>
                              <TableCell align="right"><strong>Value</strong></TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            <TableRow>
                              <TableCell>Total Wattage</TableCell>
                              <TableCell align="right">{results.totalWattage}W</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Transformer Size</TableCell>
                              <TableCell align="right">{results.transformerSize}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Transformer Cost</TableCell>
                              <TableCell align="right">${results.transformerCost}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#e3f2fd' }}>
                              <TableCell><strong>Installation Days</strong></TableCell>
                              <TableCell align="right"><strong>{results.installDays}</strong></TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Trenching Hours</TableCell>
                              <TableCell align="right">{results.trenchingHours}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Material Cost</TableCell>
                              <TableCell align="right">${results.materialCost}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Installation Cost</TableCell>
                              <TableCell align="right">${results.installCost}</TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>Trenching Cost</TableCell>
                              <TableCell align="right">${results.trenchingCost}</TableCell>
                            </TableRow>
                            <TableRow style={{ backgroundColor: '#c8e6c9' }}>
                              <TableCell><strong>Total Cost</strong></TableCell>
                              <TableCell align="right"><strong>${results.totalCost}</strong></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>

                      <Box mt={3} p={2} style={{ backgroundColor: '#f5f5f5', borderRadius: 8 }}>
                        <Typography variant="body2" color="textSecondary">
                          <strong>Formula:</strong> Install Days = Lights ÷ Lights per Day<br />
                          Trenching = Lights × Hours per Light
                        </Typography>
                      </Box>
                    </Box>
                  )}

                  {Object.keys(configOverrides).length > 0 && (
                    <Box mt={3}>
                      <Alert severity="info">
                        You have {Object.keys(configOverrides).length} temporary config override(s).
                        These won't be saved to the configuration.
                      </Alert>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        )}
      </Container>
    </Box>
  )
}

export default CalculationPlayground
